import { useEffect, useState } from 'react';
import { get, getDatabase, ref } from 'firebase/database';
import { CostHistoryEntry, CostHistoryKind, fetchCostHistory } from '../../api/costHistoryApi';
import { useUserNames } from '../../Helpers/useUserNames';

interface CostHistoryState {
    entries: CostHistoryEntry[];
    loading: boolean;
    error: string | null;
    userName: (uid: string) => string;
    buildingName: (buildingKey: string) => string | undefined;
}

/** A cost's history, loaded when `enabled` turns true (the modal opens), with user and building names. */
export const useCostHistory = (kind: CostHistoryKind, subjectKey: string, enabled: boolean): CostHistoryState => {
    const [entries, setEntries] = useState<CostHistoryEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [buildingNames, setBuildingNames] = useState<Record<string, string>>({});
    const { userNames, loadUserNames } = useUserNames();

    useEffect(() => {
        if (!enabled || !subjectKey) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const loaded = await fetchCostHistory(kind, subjectKey);
                if (cancelled) return;
                setEntries(loaded);

                await loadUserNames(Array.from(new Set(loaded.map(e => e.changedBy))));

                const buildingKeys = Array.from(new Set(loaded.flatMap(e => (e.buildingKey ? [e.buildingKey] : []))));
                const db = getDatabase();
                const names = await Promise.all(buildingKeys.map(async key => {
                    const snapshot = await get(ref(db, `buildingsSummary/${key}/name`));
                    return [key, snapshot.exists() ? String(snapshot.val()) : 'Okänd byggnad'] as const;
                }));
                if (!cancelled) setBuildingNames(Object.fromEntries(names));
            } catch (loadError) {
                console.error('Error loading cost history:', loadError);
                if (!cancelled) setError('Kunde inte hämta historiken. Försök igen.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [kind, subjectKey, enabled, loadUserNames]);

    return {
        entries,
        loading,
        error,
        userName: uid => userNames[uid] || 'Okänd användare',
        buildingName: key => buildingNames[key]
    };
};
